import React, { useState } from 'react';
import {
  useRollHistory,
  RollEntry,
  DamageRollEntry,
  FuryRollEntry
} from '../../stores/rollHistory';
import type { PsychicRollDetails } from '../../types/psychic';
import { useSettingsStore } from '../../stores/settings';

export function RightSidebar() {
  const { history, isOpen, toggleSidebar, clearHistory } = useRollHistory();
  const compactMode = useSettingsStore((s) => s.compactMode);

  if (!isOpen) {
    return (
      <button
        onClick={toggleSidebar}
        className="fixed right-0 top-20 bg-surface p-2 rounded-l-md shadow-md border-y border-l border-line hover:bg-surface-hover transition-colors z-20"
        title="Show Roll History"
      >
        <span className="text-xl">🎲</span>
      </button>
    );
  }

  return (
    <div
      className={`${compactMode ? 'w-80' : 'w-96'} bg-surface shadow-xl h-screen overflow-hidden flex flex-col border-l border-line transition-all duration-300 flex-shrink-0 z-20`}
    >
      {/* Header */}
      <div
        className={`${compactMode ? 'px-3 py-2' : 'p-4'} border-b border-line flex items-center justify-between`}
      >
        <h2
          className={`${compactMode ? 'text-sm' : 'text-lg'} font-bold text-ink-strong flex items-center gap-2`}
        >
          <span>🎲</span> Roll History
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={clearHistory}
            className="p-1 px-2 text-xs text-red-600 hover:bg-red-50 dark:bg-red-900/30 dark:hover:bg-red-900/20 rounded"
            title="Clear History"
          >
            Clear
          </button>
          <button
            onClick={toggleSidebar}
            className="p-1 rounded hover:bg-surface-hover transition-colors"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Rolls List */}
      <div className={`flex-1 overflow-y-auto ${compactMode ? 'p-2 space-y-2' : 'p-3 space-y-3'}`}>
        {history.length === 0 ? (
          <div className="text-center text-ink-faint py-8 italic">No rolls yet...</div>
        ) : (
          history.map((entry) => <RollEntryCard key={entry.id} entry={entry} />)
        )}
      </div>
    </div>
  );
}

function RollEntryCard({ entry }: { entry: RollEntry }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isSuccess = entry.result === 'success';
  const isFailure = entry.result === 'failure';
  const combat = entry.combatDetails;

  const borderColor = isSuccess
    ? 'border-green-500'
    : isFailure
      ? 'border-red-500'
      : 'border-blue-300';

  const bgColor = isSuccess
    ? 'bg-green-50 dark:bg-green-900/10'
    : isFailure
      ? 'bg-red-50 dark:bg-red-900/10'
      : 'bg-surface-alt';

  // If we have comprehensive combat details, render the new format
  if (combat) {
    return (
      <div
        className={`rounded-lg border-l-4 ${borderColor} ${bgColor} shadow-sm animate-fade-in overflow-hidden`}
      >
        {/* Header - Always visible */}
        <div
          className="p-3 cursor-pointer hover:bg-surface-hover"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
              <span className="font-bold text-sm text-ink">{entry.title}</span>
            </div>
            <span className="text-xs text-ink-faint">
              {new Date(entry.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          </div>

          {/* Quick Summary */}
          <div className="flex items-center gap-3 mt-2">
            <div
              className={`text-xs font-bold px-2 py-1 rounded ${
                combat.isJammed
                  ? 'bg-orange-500 text-white'
                  : combat.success
                    ? 'bg-green-600 text-white'
                    : 'bg-red-600 text-white'
              }`}
            >
              {combat.isJammed ? '🔴 JAMMED' : combat.success ? '✓ HIT' : '✗ MISS'}
            </div>
            {combat.success && !combat.isJammed && (
              <span className="text-lg font-bold text-red-600 dark:text-red-400">
                {combat.totalDamage} DMG
              </span>
            )}
            {combat.totalHits && combat.totalHits > 1 && (
              <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-0.5 rounded">
                {combat.totalHits} hits
              </span>
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t border-line p-3 space-y-3 text-sm">
            {/* Attack Roll Section */}
            <div className="bg-surface rounded p-2 border border-line">
              <div className="font-semibold text-ink mb-1 flex items-center gap-1">
                🎯 Attack Roll
              </div>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-mono font-bold">{combat.attackRoll}</span>
                <span className="text-ink-dim">
                  vs <span className="font-semibold">{combat.attackTarget}</span>
                </span>
                <span
                  className={`font-semibold ${combat.degrees >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {combat.degrees >= 0 ? '+' : ''}
                  {combat.degrees} DoS
                </span>
              </div>

              {/* Modifiers */}
              {combat.attackModifiers.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-ink-faint hover:text-ink">
                    Modifiers ({combat.attackModifiers.length})
                  </summary>
                  <ul className="mt-1 space-y-0.5 pl-2">
                    {combat.attackModifiers.map((mod, idx) => (
                      <li
                        key={idx}
                        className={
                          mod.isProficiencyPenalty
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-ink-dim'
                        }
                      >
                        {mod.isProficiencyPenalty && '⚠️ '}
                        {mod.name}: {mod.value >= 0 ? '+' : ''}
                        {mod.value}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            {/* Damage Rolls Section */}
            {combat.damageRolls.length > 0 && (
              <div className="space-y-2">
                {combat.damageRolls.map((dmg, idx) => (
                  <DamageRollCard key={idx} damage={dmg} hitNumber={idx + 1} />
                ))}
              </div>
            )}

            {/* Total Damage Summary */}
            {combat.success && !combat.isJammed && combat.damageRolls.length > 0 && (
              <div className="bg-red-100 dark:bg-red-900/30 rounded p-2 border border-red-300 dark:border-red-700">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-red-800 dark:text-red-200">Total Damage</span>
                  <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {combat.totalDamage}
                  </span>
                </div>
                {combat.totalFuryDamage > 0 && (
                  <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                    Includes +{combat.totalFuryDamage} from Righteous Fury
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Psychic roll entries with psychicDetails
  if (entry.psychicDetails) {
    return <PsychicRollEntryCard entry={entry} psychicDetails={entry.psychicDetails} />;
  }

  // Fallback to legacy format for non-combat rolls
  return (
    <div
      className={`p-3 rounded-lg border-l-4 ${borderColor} ${bgColor} shadow-sm animate-fade-in`}
    >
      <div className="flex justify-between items-start mb-1">
        <span className="font-bold text-sm text-ink">{entry.title}</span>
        <span className="text-xs text-ink-faint">
          {new Date(entry.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}
        </span>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="text-2xl font-bold font-mono text-ink">{entry.total}</div>
        <div
          className={`text-xs font-semibold px-2 py-1 rounded ${
            isSuccess
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : isFailure
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : 'bg-surface-alt text-ink'
          }`}
        >
          {entry.details}
        </div>
      </div>

      {entry.relatedRolls && entry.relatedRolls.length > 0 && (
        <div className="mt-2 space-y-1">
          {entry.relatedRolls.map((roll, index) => (
            <RelatedRollRow key={index} roll={roll} />
          ))}
        </div>
      )}

      {entry.tags && entry.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {entry.tags.map((tag) => (
            <span key={tag} className="text-[10px] px-1 bg-surface-alt text-ink-dim rounded">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RelatedRollRow({ roll }: { roll: NonNullable<RollEntry['relatedRolls']>[number] }) {
  const isBurstHit = /^Hit \d+:/.test(roll.title);

  return (
    <div className="flex items-center justify-between bg-surface p-2 rounded border border-line text-xs shadow-sm">
      {isBurstHit ? (
        <div className="flex items-baseline gap-1 whitespace-nowrap">
          <span className="font-semibold text-ink">{roll.title}</span>
          <span className="text-ink-faint">|</span>
          <span className="font-mono font-bold text-lg">{roll.total}</span>
          <span className="text-ink-faint">{roll.details}</span>
        </div>
      ) : (
        <>
          <span className="font-semibold text-ink">{roll.title}</span>
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-lg">{roll.total}</span>
            <span className="text-ink-faint">{roll.details}</span>
          </div>
        </>
      )}
    </div>
  );
}

function DamageRollCard({ damage, hitNumber }: { damage: DamageRollEntry; hitNumber: number }) {
  const isRerolled = damage.wasRerolled;

  return (
    <div
      className={`rounded p-2 border ${
        isRerolled ? 'bg-surface-alt border-line opacity-60' : 'bg-surface border-line'
      }`}
    >
      <div
        className={`font-semibold mb-1 flex items-center justify-between ${
          isRerolled ? 'text-ink-faint' : 'text-ink'
        }`}
      >
        <span className="flex items-center gap-1">
          {isRerolled ? '🔄' : '💥'} {damage.title || `Hit ${hitNumber} Damage`}
          {isRerolled && (
            <span className="text-xs bg-surface-alt text-ink-faint px-1.5 py-0.5 rounded ml-1">
              SUPERSEDED
            </span>
          )}
          {damage.hitLocation && !isRerolled && (
            <span className="text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded ml-1">
              {damage.hitLocation}
            </span>
          )}
        </span>
        <span
          className={`text-xl font-bold ${
            isRerolled ? 'text-ink-faint line-through' : 'text-red-600 dark:text-red-400'
          }`}
        >
          {damage.total}
        </span>
      </div>

      {/* Damage Breakdown */}
      <div className={`text-xs space-y-0.5 ${isRerolled ? 'text-ink-faint' : 'text-ink-dim'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span>
            Die:{' '}
            <span className={`font-mono font-semibold ${isRerolled ? 'line-through' : ''}`}>
              {damage.dieRoll}
            </span>
          </span>
          <span className="text-ink-faint">|</span>
          <span>Base: {damage.baseDamage}</span>
          {damage.strengthBonus > 0 && (
            <>
              <span className="text-ink-faint">|</span>
              <span>Str: +{damage.strengthBonus}</span>
            </>
          )}
          {damage.modifiers.length > 0 && (
            <>
              <span className="text-ink-faint">|</span>
              {damage.modifiers.map((mod, idx) => (
                <span key={idx}>
                  {mod.name}: {mod.value >= 0 ? '+' : ''}
                  {mod.value}
                </span>
              ))}
            </>
          )}
        </div>
        <div>
          Penetration: <span className="font-semibold">{damage.penetration}</span>
        </div>
      </div>

      {/* Tearing Info */}
      {damage.tearingInfo && (
        <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-300 dark:border-orange-600">
          <div className="flex items-center gap-1 mb-1">
            <span>🔪</span>
            <span className="text-xs font-bold text-orange-900 dark:text-orange-100">TEARING</span>
          </div>
          <div className="text-xs text-orange-800 dark:text-orange-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                Rolled:{' '}
                <span className="font-mono font-semibold">
                  {damage.tearingInfo.allRolls.join(', ')}
                </span>
              </span>
              <span className="text-orange-400">→</span>
              <span>
                Dropped:{' '}
                <span className="font-mono font-semibold text-red-600 dark:text-red-400 line-through">
                  {damage.tearingInfo.discardedRoll}
                </span>
              </span>
              <span className="text-orange-400">→</span>
              <span>
                Kept:{' '}
                <span className="font-mono font-semibold text-green-600 dark:text-green-400">
                  {damage.tearingInfo.keptRolls.join(', ')}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Reroll reason indicator */}
      {isRerolled && damage.rerollReason && (
        <div className="mt-1 text-xs text-ink-faint italic">
          Rerolled due to: {damage.rerollReason}
        </div>
      )}

      {/* Righteous Fury */}
      {damage.furyRolls && damage.furyRolls.length > 0 && (
        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-300 dark:border-yellow-600">
          <div className="flex items-center gap-1 mb-1">
            <span>⚡</span>
            <span className="text-xs font-bold text-yellow-900 dark:text-yellow-100">
              RIGHTEOUS FURY!
            </span>
          </div>
          <div className="space-y-1">
            {damage.furyRolls.map((fury, idx) => (
              <FuryRollDisplay key={idx} fury={fury} index={idx} />
            ))}
          </div>
          <div className="mt-1 text-xs font-bold text-yellow-900 dark:text-yellow-100 border-t border-yellow-300 dark:border-yellow-600 pt-1">
            Fury Bonus: +{damage.furyRolls.reduce((sum, f) => sum + f.bonusDamage, 0)}
          </div>
        </div>
      )}
    </div>
  );
}

function FuryRollDisplay({ fury, index }: { fury: FuryRollEntry; index: number }) {
  return (
    <div className="text-xs bg-yellow-100 dark:bg-yellow-900/40 p-1 rounded flex items-center justify-between">
      <span className="text-yellow-800 dark:text-yellow-200">
        Confirm #{index + 1}: {fury.confirmRoll} vs {fury.confirmTarget}
      </span>
      <span
        className={`font-semibold ${fury.confirmSuccess ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
      >
        {fury.confirmSuccess ? `✓ +${fury.bonusDamage} DMG` : '✗ Failed'}
      </span>
    </div>
  );
}

// ─── Psychic Roll Display Helpers ───────────────────────────────────────────

const STRENGTH_LABELS: Record<string, { label: string; color: string }> = {
  fettered: {
    label: 'Fettered',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
  },
  unfettered: {
    label: 'Unfettered',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
  },
  push: {
    label: 'Push',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
  }
};

function PsychicRollEntryCard({
  entry,
  psychicDetails
}: {
  entry: RollEntry;
  psychicDetails: PsychicRollDetails;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSuccess = entry.result === 'success';
  const isFailure = entry.result === 'failure';

  const borderColor = isSuccess
    ? 'border-green-500'
    : isFailure
      ? 'border-red-500'
      : 'border-purple-400';

  const bgColor = isSuccess
    ? 'bg-green-50 dark:bg-green-900/10'
    : isFailure
      ? 'bg-red-50 dark:bg-red-900/10'
      : 'bg-purple-50 dark:bg-purple-900/10';

  const strengthStyle = STRENGTH_LABELS[psychicDetails.strengthLevel] ?? {
    label: psychicDetails.strengthLevel,
    color: 'bg-surface-alt text-ink'
  };

  return (
    <div
      className={`rounded-lg border-l-4 ${borderColor} ${bgColor} shadow-sm animate-fade-in overflow-hidden`}
    >
      {/* Header — always visible */}
      <div
        className="p-3 cursor-pointer hover:bg-surface-hover"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
            <span className="text-lg" aria-hidden="true">
              🔮
            </span>
            <span className="font-bold text-sm text-ink">{entry.title}</span>
          </div>
          <span className="text-xs text-ink-faint">
            {new Date(entry.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        </div>

        {/* Quick Summary */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {/* Success / Failure badge */}
          <span
            className={`text-xs font-bold px-2 py-1 rounded ${
              isSuccess ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {isSuccess ? '✓ SUCCESS' : '✗ FAILURE'}
          </span>

          {/* Strength level badge */}
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${strengthStyle.color}`}>
            {strengthStyle.label}
          </span>

          {/* Effective PR */}
          <span className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 px-2 py-0.5 rounded">
            PR {psychicDetails.effectivePsyRating}
          </span>

          {/* Phenomena indicator */}
          {psychicDetails.phenomenaTriggered && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 px-2 py-0.5 rounded font-medium">
              {psychicDetails.perilsResult ? '💀 Perils!' : '⚠️ Phenomena'}
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-line p-3 space-y-3 text-sm">
          {/* Roll Details */}
          <div className="bg-surface rounded p-2 border border-line">
            <div className="font-semibold text-ink mb-1 flex items-center gap-1">
              🔮 Focus Power Test
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-mono font-bold">{entry.total}</span>
              <span className="text-ink-dim">{entry.details}</span>
            </div>
            <div className="text-xs text-ink-faint mt-1">
              Base PR: {psychicDetails.basePsyRating} → Effective PR:{' '}
              {psychicDetails.effectivePsyRating} (+{psychicDetails.effectivePsyRating * 5} bonus)
            </div>
          </div>

          {/* Related Rolls (modifiers, etc.) */}
          {entry.relatedRolls && entry.relatedRolls.length > 0 && (
            <div className="space-y-1">
              {entry.relatedRolls.map((roll, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-surface p-2 rounded border border-line text-xs shadow-sm"
                >
                  <span className="font-semibold text-ink">{roll.title}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-lg">{roll.total}</span>
                    <span className="text-ink-faint">{roll.details}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Phenomena Result */}
          {psychicDetails.phenomenaTriggered && psychicDetails.phenomenaResult && (
            <PsychicPhenomenaInline phenomenaResult={psychicDetails.phenomenaResult} />
          )}

          {/* Perils Result */}
          {psychicDetails.perilsResult && (
            <PsychicPerilsInline perilsResult={psychicDetails.perilsResult} />
          )}

          {/* Tags */}
          {entry.tags && entry.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {entry.tags.map((tag) => (
                <span key={tag} className="text-[10px] px-1 bg-surface-alt text-ink-dim rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PsychicPhenomenaInline({
  phenomenaResult
}: {
  phenomenaResult: NonNullable<PsychicRollDetails['phenomenaResult']>;
}) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 rounded p-2 border border-orange-300 dark:border-orange-600">
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="w-full text-left flex items-center justify-between min-h-[36px]"
      >
        <div className="flex items-center gap-1">
          <span aria-hidden="true">⚠️</span>
          <span className="text-xs font-bold text-orange-900 dark:text-orange-100">
            PSYCHIC PHENOMENA
          </span>
        </div>
        <span
          className={`text-xs text-orange-700 dark:text-orange-300 transition-transform ${showDetails ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          ▼
        </span>
      </button>
      <div className="text-xs text-orange-800 dark:text-orange-200 mt-1">
        <span className="font-semibold">{phenomenaResult.effectName}</span>
        <span className="text-orange-600 dark:text-orange-400 ml-2">
          (Roll: {phenomenaResult.roll} → {phenomenaResult.finalRoll})
        </span>
      </div>
      {showDetails && (
        <div className="mt-1 text-xs text-orange-700 dark:text-orange-300 border-t border-orange-200 dark:border-orange-700 pt-1">
          Roll: {phenomenaResult.roll}, Final: {phenomenaResult.finalRoll}
        </div>
      )}
    </div>
  );
}

function PsychicPerilsInline({
  perilsResult
}: {
  perilsResult: NonNullable<PsychicRollDetails['perilsResult']>;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const isAstropath = perilsResult.rolls.length === 3;

  return (
    <div className="bg-red-50 dark:bg-red-900/20 rounded p-2 border-2 border-red-300 dark:border-red-600">
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="w-full text-left flex items-center justify-between min-h-[36px]"
      >
        <div className="flex items-center gap-1">
          <span aria-hidden="true">💀</span>
          <span className="text-xs font-bold text-red-900 dark:text-red-100">
            PERILS OF THE WARP
          </span>
        </div>
        <span
          className={`text-xs text-red-700 dark:text-red-300 transition-transform ${showDetails ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          ▼
        </span>
      </button>
      <div className="text-xs text-red-800 dark:text-red-200 mt-1">
        <span className="font-semibold">{perilsResult.effectName}</span>
        <span className="text-red-600 dark:text-red-400 ml-2">
          (Final: {perilsResult.finalRoll})
        </span>
      </div>
      {showDetails && (
        <div className="mt-1 text-xs text-red-700 dark:text-red-300 border-t border-red-200 dark:border-red-700 pt-1 space-y-0.5">
          <div>
            Rolls: [
            {perilsResult.rolls.map((r, i) => (
              <span key={i}>
                {i > 0 && ', '}
                {isAstropath && r === perilsResult.discardedRoll ? (
                  <span className="line-through opacity-50">{r}</span>
                ) : (
                  <span>{r}</span>
                )}
              </span>
            ))}
            ]
            {isAstropath && perilsResult.discardedRoll !== undefined && (
              <span className="ml-1">(Astropath discarded {perilsResult.discardedRoll})</span>
            )}
          </div>
          <div>Final: {perilsResult.finalRoll}</div>
        </div>
      )}
    </div>
  );
}
